import { z } from 'zod';

const text = z.string().min(1);
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);

/** Separate from the D00 replay protocol: additive commands for the playable slice. */
export const StoryCommandSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('visit'), beatId: text }),
  z.strictObject({ type: z.literal('inspect'), interactionId: text }),
  z.strictObject({ type: z.literal('talk'), speakerId: text }),
  z.strictObject({ type: z.literal('choose'), optionId: text }),
  z.strictObject({ type: z.literal('leave-dialogue') }),
]);
export type StoryCommand = z.infer<typeof StoryCommandSchema>;

export const StoryLineSchema = z.strictObject({ speakerId: text, speaker: text, textId: text, text });
export type StoryLine = z.infer<typeof StoryLineSchema>;
export const StoryJournalEntrySchema = z.strictObject({
  id: text, kind: z.enum(['discovery', 'choice', 'cost', 'aftermath']), text,
});
export type StoryJournalEntry = z.infer<typeof StoryJournalEntrySchema>;
export const StoryEventSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('line'), sequence: count, line: StoryLineSchema }),
  z.strictObject({ type: z.literal('journal'), sequence: count, entry: StoryJournalEntrySchema }),
  z.strictObject({ type: z.literal('visited'), sequence: count, beatId: text }),
  z.strictObject({ type: z.literal('combat-started'), sequence: count }),
  z.strictObject({ type: z.literal('combat-ended'), sequence: count, result: z.enum(['won', 'died']) }),
]);
export type StoryEvent = z.infer<typeof StoryEventSchema>;

/** What can be seen before committing to a fight. No private memories or predicted outcome. */
export const StoryDangerSchema = z.strictObject({
  encounterId: text, tell: text, alternative: text,
  company: z.array(z.strictObject({ name: text, joins: z.boolean(), reason: text.nullable() })),
});
export type StoryDanger = z.infer<typeof StoryDangerSchema>;

export const StoryViewSchema = z.strictObject({
  version: z.literal(1), beatId: text, beatName: text, description: text,
  milestone: z.enum(['slice.M0', 'slice.M1', 'slice.M2', 'slice.M3', 'slice.M4', 'slice.M5']),
  objective: text, next: z.array(z.strictObject({ beatId: text, targetId: text, label: text })),
  zone: text,
  // A journey when an exit's zone differs from the view's; its distance comes from the route (recipes/arcs/unburied/chapters/README.md).
  exits: z.array(z.strictObject({ beatId: text, label: text, zone: text, distanceKm: z.number().positive().nullable(), enabled: z.boolean(), reason: text.nullable() })),
  interactions: z.array(z.strictObject({ id: text, label: text, enabled: z.boolean(), reason: text.nullable(), costCuts: count, danger: StoryDangerSchema.optional() })),
  speakers: z.array(z.strictObject({ id: text, name: text })),
  dialogue: z.strictObject({ speakerId: text, lines: z.array(StoryLineSchema), options: z.array(z.strictObject({
    id: text, label: text, intention: text, enabled: z.boolean(), reason: text.nullable(), costCuts: count, consequence: text, danger: StoryDangerSchema.optional(),
  })) }).nullable(),
  journal: z.array(StoryJournalEntrySchema), purseCuts: count,
  world: z.strictObject({
    carts: z.enum(['waiting', 'porters', 'moving', 'rationed']),
    shutters: z.enum(['open', 'closed']), crowd: z.enum(['hungry', 'working', 'relieved', 'frightened']),
    gate: z.enum(['concession-guards', 'public-custody', 'workers', 'supervised', 'unattended']),
    removal: z.enum(['quiet', 'loading', 'held']),
    activeBodies: count, destroyedBodies: count, breadPriceCuts: count, saltPriceCuts: count,
    melian: z.enum(['under-suspicion', 'accused', 'cleared']),
    notices: z.array(text),
  }),
  companions: z.array(z.strictObject({ id: text, name: text, obligation: text, status: z.enum(['available', 'hired', 'departed', 'dead']), judgment: text.nullable(), paidCuts: count })),
  outcomeId: text.nullable(), complete: z.boolean(), combatActive: z.boolean(), dead: z.boolean(),
});
export type StoryView = z.infer<typeof StoryViewSchema>;
